import cx from 'classnames';
import { FunctionComponent } from 'react';

import AccumulationLabel, {
  TAccumulationLabelProps,
} from '../../../ui/v2/accumulation-label';
import { getAccumulationTense, useProductAccumulation } from '../use-accumulation';

export type TProductAccumulationProps = {
  /** Only the `points` block is used — see `useProductAccumulation`. */
  pricing?: { points?: TPricingPoints | null } | null;
  /** `STO` | `SOL` | `RSV`, when the surface knows it. */
  status?: string | null;
  /**
   * Post-purchase screens (thank-you, order pages): switches the label to the
   * past tense and stops `RSV` — "reserved for this order" — from blocking it.
   */
  afterPurchase?: boolean;
  /**
   * Order `status_code`, on post-purchase screens: `Budget`/`Payment` mean the
   * wallet has not been credited yet, so the label stays in the future tense.
   */
  statusCode?: string | null;
  variant?: TAccumulationLabelProps['variant'];
  size?: TAccumulationLabelProps['size'];
  /** `PLUS` replaces the wording with a `+` — see `AccumulationLabel`. */
  label?: TAccumulationLabelProps['label'];
  className?: string;
};

/**
 * A single product's accumulation label. Exists as a component so it can be used
 * inside a `products.map()` — `useProductAccumulation` is a hook and cannot be
 * called per iteration — and renders nothing when the product accumulates
 * nothing, so no guard is needed at the call site.
 */
const ProductAccumulation: FunctionComponent<TProductAccumulationProps> = ({
  pricing,
  status,
  afterPurchase = false,
  statusCode,
  variant,
  size,
  label,
  className,
}) => {
  const accumulation = useProductAccumulation(pricing, status, { afterPurchase });

  if (!accumulation) {
    return null;
  }

  return (
    <AccumulationLabel
      amount={accumulation}
      tense={getAccumulationTense(statusCode, { afterPurchase })}
      variant={variant}
      size={size}
      label={label}
      className={cx('w-fit', className)}
    />
  );
};

export default ProductAccumulation;
